import { ReactElement, useEffect } from "react";
import { Button, VStack, useTheme } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../../components/atoms/Layout";
import { ENABLE_FILE_ANALYSIS, ENABLED_ENGINES } from "../../environments";
import { useAtom } from "jotai";
import {
  accessTokenInfoAtom,
  engineTypesAtom,
  EngineTypesMap,
} from "../../store";
import { AnalysisEngineType } from "../../types";

// エンジン選択後に最初に表示する解析画面のパス
export function analysisEntryPath(): string {
  return ENABLE_FILE_ANALYSIS ? "select-recording" : "recording";
}

export function getSelectableEngineList(
  engineTypes: EngineTypesMap,
): AnalysisEngineType[] {
  const hasEngines = (
    condition:
      { and_: (keyof EngineTypesMap)[] } | { or_: (keyof EngineTypesMap)[] },
  ): boolean => {
    if ("and_" in condition) {
      return condition.and_.every((key) => engineTypes[key] === true);
    }

    return condition.or_.some((key) => engineTypes[key] === true);
  };

  const options: { enabled: boolean; engine: AnalysisEngineType }[] = [
    { enabled: false, engine: "Cognitive" },
    { enabled: hasEngines({ or_: ["cog", "co1"] }), engine: "cog" },
    { enabled: hasEngines({ or_: ["co1"] }), engine: "co1" },
    { enabled: hasEngines({ or_: ["np1"] }), engine: "np1" },
    { enabled: hasEngines({ or_: ["dysphagia"] }), engine: "dysphagia" },
    { enabled: hasEngines({ or_: ["mi1"] }), engine: "mi1" },
    { enabled: hasEngines({ and_: ["mi1", "mi2.1"] }), engine: "Mi1_Mi2" },
    { enabled: hasEngines({ or_: ["mi2.1"] }), engine: "mi2.1" },
    { enabled: hasEngines({ or_: ["co2.1"] }), engine: "co2.1" },
    { enabled: hasEngines({ or_: ["br1"] }), engine: "br1" },
    { enabled: false, engine: "UIX" },
    { enabled: hasEngines({ or_: ["co1"] }), engine: "Co1Demo" },
    { enabled: hasEngines({ or_: ["mi1"] }), engine: "NTT" },
    { enabled: hasEngines({ or_: ["sl1"] }), engine: "sl1" },
    { enabled: hasEngines({ or_: ["mi2"] }), engine: "Mi2Demo" },
    { enabled: hasEngines({ or_: ["co2"] }), engine: "Co2Demo" },
    { enabled: hasEngines({ and_: ["mi2", "co2"] }), engine: "TFH" },
    { enabled: hasEngines({ or_: ["sl1"] }), engine: "TokioDRSl1" },
    {
      enabled: hasEngines({ or_: ["cog", "co1"] }),
      engine: "TokioDRCognitive",
    },
  ];

  return options
    .filter((option) => {
      const engines = ENABLED_ENGINES as AnalysisEngineType[];
      return engines.includes(option.engine) && option.enabled;
    })
    .map((option) => {
      return option.engine;
    });
}

function EngineSelectionButton({
  engineKey,
}: Readonly<{
  engineKey: string;
}>): ReactElement {
  const { t } = useTranslation();
  return (
    <Button
      as={Link}
      data-testid={`engine-select-${engineKey}`}
      to={`./${engineKey}/${analysisEntryPath()}`}
      width="full"
      variant="btn_primary"
      size="lg"
    >
      {t(`EngineSelection.${engineKey}`)}
    </Button>
  );
}

export function Engines(): ReactElement {
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  if (tokenInfo === null) throw new Error("no token");

  const [engineTypes] = useAtom(engineTypesAtom);
  const url_list = getSelectableEngineList(engineTypes);

  const theme = useTheme();
  const backgroundColor = theme.colors.primary["bg_lv1"];

  useEffect(() => {
    document.body.style.backgroundColor = backgroundColor;
    return () => {
      document.body.style.backgroundColor = "white";
    };
  }, [backgroundColor]);

  return (
    <Layout h="full">
      <VStack justify="center" minH="full" gap={12}>
        {url_list.includes("mi1") && <EngineSelectionButton engineKey="mi1" />}
        {url_list.includes("mi2") && <EngineSelectionButton engineKey="mi2" />}
        {url_list.includes("mi2.1") && (
          <EngineSelectionButton engineKey="mi2.1" />
        )}
        {url_list.includes("Mi1_Mi2") && (
          <EngineSelectionButton engineKey="Mi1_Mi2" />
        )}
        {url_list.includes("co2.1") && (
          <EngineSelectionButton engineKey="co2.1" />
        )}
        {url_list.includes("Cognitive") && (
          <EngineSelectionButton engineKey="Cognitive" />
        )}
        {url_list.includes("Co1Demo") && (
          <EngineSelectionButton engineKey="Co1Demo" />
        )}
        {url_list.includes("Co2Demo") && (
          <EngineSelectionButton engineKey="Co2Demo" />
        )}
        {url_list.includes("cog") && <EngineSelectionButton engineKey="cog" />}
        {url_list.includes("co1") && <EngineSelectionButton engineKey="co1" />}
        {url_list.includes("sl1") && <EngineSelectionButton engineKey="sl1" />}

        {url_list.includes("Mi2Demo") && (
          <EngineSelectionButton engineKey="Mi2Demo" />
        )}
        {url_list.includes("np1") && <EngineSelectionButton engineKey="np1" />}
        {url_list.includes("dysphagia") && (
          <EngineSelectionButton engineKey="dysphagia" />
        )}
        {url_list.includes("br1") && <EngineSelectionButton engineKey="br1" />}
        {url_list.includes("UIX") && <EngineSelectionButton engineKey="UIX" />}
        {url_list.includes("NTT") && <EngineSelectionButton engineKey="NTT" />}
        {url_list.includes("TFH") && <EngineSelectionButton engineKey="TFH" />}
        {url_list.includes("TokioDRCognitive") && (
          <EngineSelectionButton engineKey="TokioDRCognitive" />
        )}
        {url_list.includes("TokioDRSl1") && (
          <EngineSelectionButton engineKey="TokioDRSl1" />
        )}
      </VStack>
    </Layout>
  );
}
